import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';
import BrandLogoImage from '../BrandLogoImage';
import { getProductImageUrl, useProductImageFallback } from '../../utils/productImages';
import { getRecommendationSignals } from '../../utils/productViewHistory';
import { getHomeFaqs } from '../../utils/seoContent';
import { getProductPath } from '../../utils/productUrls';
import { useLanguage } from '../../LanguageContext';

import heroImage from '../../assets/images/Banner2.avif';
import secondaryImage from '../../assets/images/Industrial.jpg';
import facilityImage from '../../assets/images/VIS.jpg';
import automationImage from '../../assets/images/Automation1.jpeg';
import roboticsImage from '../../assets/images/Robotics.jpg';
import industryImage from '../../assets/images/Industry.jpg';
import plcImage from '../../assets/images/PLC.jpg';

import omronLogo from '../../assets/images/OMRON.png';
import abbLogo from '../../assets/images/ABB.png';
import sickLogo from '../../assets/images/SICK.png';
import siemensLogo from '../../assets/images/Siemens.png';
import smcLogo from '../../assets/images/SMC.png';
import phoenixLogo from '../../assets/images/Phoenix_Contact.png';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';

const brandCollections = [
  {
    name: 'OMRON',
    logo: omronLogo,
    categories: [['Photoelectric sensors', 'Sensores fotoeléctricos'], ['Safety controls', 'Controles de seguridad'], ['PLCs & HMIs', 'PLC y HMI']],
    query: 'OMRON',
    tone: 'blue'
  },
  {
    name: 'ABB',
    logo: abbLogo,
    categories: [['Variable frequency drives', 'Variadores de frecuencia'], ['Motor protection', 'Protección de motores'], ['Robotics', 'Robótica']],
    query: 'ABB',
    tone: 'red'
  },
  {
    name: 'SICK',
    logo: sickLogo,
    categories: [['Vision sensors', 'Sensores de visión'], ['Safety scanners', 'Escáneres de seguridad'], ['Encoders', 'Encoders']],
    query: 'SICK',
    tone: 'cyan'
  },
  {
    name: 'Siemens',
    logo: siemensLogo,
    categories: [['SIMATIC controls', 'Controles SIMATIC'], ['Industrial networking', 'Redes industriales'], ['Power supplies', 'Fuentes de alimentación']],
    query: 'Siemens',
    tone: 'teal'
  },
  {
    name: 'SMC',
    logo: smcLogo,
    categories: [['Pneumatic valves', 'Válvulas neumáticas'], ['Air preparation', 'Preparación de aire'], ['Actuators', 'Actuadores']],
    query: 'SMC',
    tone: 'navy'
  },
  {
    name: 'Phoenix Contact',
    logo: phoenixLogo,
    categories: [['Terminal blocks', 'Bloques de terminales'], ['Relays', 'Relés'], ['Industrial connectivity', 'Conectividad industrial']],
    query: 'Phoenix Contact',
    tone: 'green'
  }
];

const applications = [
  {
    title: ['Factory automation', 'Automatización de fábricas'],
    description: ['Control, sensing and connectivity for reliable production lines.', 'Control, detección y conectividad para líneas de producción confiables.'],
    image: automationImage,
    link: '/info#automation',
    number: '01'
  },
  {
    title: ['Robotics & motion', 'Robótica y movimiento'],
    description: ['Precision components for movement, positioning and safety.', 'Componentes de precisión para movimiento, posicionamiento y seguridad.'],
    image: roboticsImage,
    link: '/info#robotics',
    number: '02'
  },
  {
    title: ['Industry 4.0', 'Industria 4.0'],
    description: ['Connected infrastructure for better visibility and decisions.', 'Infraestructura conectada para mejorar la visibilidad y la toma de decisiones.'],
    image: industryImage,
    link: '/info#industry4',
    number: '03'
  },
  {
    title: ['PLC & control', 'PLC y control'],
    description: ['Scalable control platforms for mission-critical operations.', 'Plataformas de control escalables para operaciones de misión crítica.'],
    image: plcImage,
    link: '/info#plc',
    number: '04'
  }
];

const resources = [
  {
    type: ['Service guide', 'Guía de servicios'],
    title: ['Build a sourcing plan around your operation', 'Cree un plan de abastecimiento para su operación'],
    description: ['See how our team supports product selection, cross-references and project requirements.', 'Conozca cómo nuestro equipo apoya la selección de productos, referencias cruzadas y requisitos de proyectos.'],
    href: '/services',
    accent: 'orange'
  },
  {
    type: ['Product updates', 'Novedades de productos'],
    title: ['New technology for the factory floor', 'Nueva tecnología para la planta'],
    description: ['Explore product announcements, application ideas and practical automation updates.', 'Explore lanzamientos, ideas de aplicación y novedades prácticas de automatización.'],
    href: '/products-news',
    accent: 'blue'
  },
  {
    type: ['Knowledge center', 'Centro de conocimiento'],
    title: ['Industrial insights, made useful', 'Conocimiento industrial que sí es útil'],
    description: ['Straightforward articles for engineers, buyers and maintenance teams.', 'Artículos claros para ingenieros, compradores y equipos de mantenimiento.'],
    href: '/blogs',
    accent: 'ink'
  }
];

const getProductsFromResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.products)) return data.products;
  return [];
};

const formatPrice = (price, language) => {
  const parsed = Number.parseFloat(price);
  if (!Number.isFinite(parsed) || parsed <= 0) return language === 'es' ? 'Solicitar precio' : 'Request pricing';
  return parsed.toLocaleString(language === 'es' ? 'es-HN' : 'en-US', {
    style: 'currency',
    currency: 'USD'
  });
};

function FeaturedProducts() {
  const { language, t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    async function loadProducts() {
      try {
        const response = await fetch(
          `${API_BASE}/api/products/search?q=*&page=1&limit=8&inStock=true&sort=product_rank_asc`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        const data = await response.json();
        setProducts(getProductsFromResponse(data).slice(0, 8));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to load featured products:', error);
          setLoadFailed(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadProducts();
    return () => controller.abort();
  }, []);

  return (
    <section id="InStockBrands" className="vh-section vh-products-section">
      <div className="vh-container">
        <div className="vh-section-heading vh-heading-row">
          <div>
            <span className="vh-eyebrow">{t('Available now', 'Disponibles ahora')}</span>
            <h2>{t('Parts that keep production moving.', 'Componentes que mantienen su producción en marcha.')}</h2>
            <p>{t('Browse a live selection of in-stock automation and control products.', 'Explore una selección actualizada de productos de automatización y control en existencia.')}</p>
          </div>
          <Link className="vh-text-link" to="/search?q=*&inStock=true&page=1">
            {t('View all in-stock products', 'Ver todos los productos en existencia')} <span aria-hidden="true">↗</span>
          </Link>
        </div>

        {loading ? (
          <div className="vh-product-grid" aria-label={t('Loading featured products', 'Cargando productos destacados')}>
            {[0, 1, 2, 3].map((item) => (
              <div className="vh-product-card vh-product-skeleton" key={item}>
                <div className="vh-skeleton-image" />
                <div className="vh-skeleton-line short" />
                <div className="vh-skeleton-line" />
                <div className="vh-skeleton-line medium" />
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="vh-product-grid">
            {products.map((product) => {
              const stock = Number.parseInt(product.product_stock, 10) || 0;
              return (
                <Link
                  className="vh-product-card"
                  key={product.idproducts || product.product_name}
                  to={getProductPath(product)}
                >
                  <div className="vh-product-image-wrap">
                    <span className="vh-stock-badge">
                      <span className="vh-stock-dot" />
                      {stock > 0 ? t(`${stock} in stock`, `${stock} en existencia`) : t('Available', 'Disponible')}
                    </span>
                    <img
                      src={getProductImageUrl(product.product_image)}
                      alt={product.product_name}
                      loading="lazy"
                      onError={useProductImageFallback}
                    />
                  </div>
                  <div className="vh-product-meta">
                    <span>{product.product_brand || t('Industrial automation', 'Automatización industrial')}</span>
                    <span>{t('Ready to quote', 'Listo para cotizar')}</span>
                  </div>
                  <h3>{product.product_name}</h3>
                  <div className="vh-product-footer">
                    <strong>{formatPrice(product.product_price, language)}</strong>
                    <span className="vh-round-arrow" aria-hidden="true">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="vh-product-unavailable">
            <div>
              <span className="vh-eyebrow">{loadFailed
                ? t('Live inventory temporarily unavailable', 'Inventario en vivo temporalmente no disponible')
                : t('Inventory update in progress', 'Actualización de inventario en curso')}
              </span>
              <h3>{t('Our full catalog is still ready to explore.', 'Nuestro catálogo completo sigue disponible para explorar.')}</h3>
              <p>{t('Search by part number, product family or manufacturer, or send us your list for a fast quote.', 'Busque por número de parte, familia de productos o fabricante, o envíenos su lista para recibir una cotización rápida.')}</p>
            </div>
            <div className="vh-unavailable-actions">
              <Link className="vh-button vh-button-dark" to="/search?q=*">{t('Browse the catalog', 'Explorar el catálogo')}</Link>
              <Link className="vh-button vh-button-outline" to="/about#contact">{t('Send a parts list', 'Enviar una lista de partes')}</Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function RecommendedProducts() {
  const { language, t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [personalized, setPersonalized] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    async function loadRecommendations() {
      const { brands, categories, viewedProductIds } = getRecommendationSignals();
      const params = new URLSearchParams({ limit: '8' });
      brands.forEach((brand) => params.append('brands', brand));
      categories.forEach((category) => params.append('categories', category));
      viewedProductIds.forEach((id) => params.append('viewedProductIds', String(id)));

      try {
        const response = await fetch(`${API_BASE}/api/products/recommendations?${params}`, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
        const data = await response.json();
        setProducts(getProductsFromResponse(data).slice(0, 8));
        setPersonalized(Boolean(data.personalized));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to load product recommendations:', error);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadRecommendations();
    return () => controller.abort();
  }, []);

  if (!loading && products.length === 0) return null;

  return (
    <section className="vh-section vh-recommended-section" aria-labelledby="recommended-products-title">
      <div className="vh-container">
        <div className="vh-section-heading vh-heading-row">
          <div>
            <span className="vh-eyebrow">{t('Recommended for you', 'Recomendados para usted')}</span>
            <h2 id="recommended-products-title">{t('More parts worth a closer look.', 'Más componentes que vale la pena conocer.')}</h2>
            <p>
              {personalized
                ? t('Selected from the brands and product categories you have explored.', 'Seleccionados según las marcas y categorías de productos que ha explorado.')
                : t('A fresh selection of in-stock products to help you get started.', 'Una nueva selección de productos en existencia para ayudarle a comenzar.')}
            </p>
          </div>
          <Link className="vh-text-link" to="/search?q=*&inStock=true&page=1">
            {t('Explore more products', 'Explorar más productos')} <span aria-hidden="true">↗</span>
          </Link>
        </div>

        {loading ? (
          <div className="vh-product-grid" aria-label={t('Loading recommended products', 'Cargando productos recomendados')}>
            {[0, 1, 2, 3].map((item) => (
              <div className="vh-product-card vh-product-skeleton" key={item}>
                <div className="vh-skeleton-image" />
                <div className="vh-skeleton-line short" />
                <div className="vh-skeleton-line" />
                <div className="vh-skeleton-line medium" />
              </div>
            ))}
          </div>
        ) : (
          <div className="vh-product-grid">
            {products.map((product) => {
              const stock = Number.parseInt(product.product_stock, 10) || 0;
              return (
                <Link
                  className="vh-product-card"
                  key={product.idproducts || product.product_name}
                  to={getProductPath(product)}
                >
                  <div className="vh-product-image-wrap">
                    <span className="vh-stock-badge">
                      <span className="vh-stock-dot" />
                      {t(`${stock} in stock`, `${stock} en existencia`)}
                    </span>
                    <img
                      src={getProductImageUrl(product.product_image)}
                      alt={product.product_name}
                      loading="lazy"
                      onError={useProductImageFallback}
                    />
                  </div>
                  <div className="vh-product-meta">
                    <span>{product.product_brand || t('Industrial automation', 'Automatización industrial')}</span>
                    <span>{product.product_category || t('Recommended', 'Recomendado')}</span>
                  </div>
                  <h3>{product.product_name}</h3>
                  <div className="vh-product-footer">
                    <strong>{formatPrice(product.product_price, language)}</strong>
                    <span className="vh-round-arrow" aria-hidden="true">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function StockBrandLogo({ brand }) {
  const [hasLogo, setHasLogo] = useState(false);

  return (
    <div className={`vh-stock-brand-logo ${hasLogo ? 'has-logo' : ''}`}>
      {!hasLogo && <span>{String(brand.brand_name || 'Brand').slice(0, 2).toUpperCase()}</span>}
      <BrandLogoImage
        brand={brand}
        onLoad={() => setHasLogo(true)}
        onExhausted={() => setHasLogo(false)}
      />
    </div>
  );
}

function InStockBrandShowcase() {
  const { t } = useLanguage();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    async function loadInStockBrands() {
      try {
        const response = await fetch(`${API_BASE}/api/brands/instock`, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Brand request failed: ${response.status}`);
        const data = await response.json();
        const availableBrands = (Array.isArray(data) ? data : []).filter((brand) => {
          const explicitCount = brand.in_stock_count ?? brand.product_count ?? brand.stock_count;
          return explicitCount === undefined || Number(explicitCount) > 0;
        });
        setBrands(availableBrands.slice(0, 8));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to load in-stock brands:', error);
          setFailed(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadInStockBrands();
    return () => controller.abort();
  }, []);

  if (!loading && (failed || brands.length === 0)) return null;

  return (
    <section className="vh-section vh-stock-brands-section">
      <div className="vh-container">
        <div className="vh-section-heading vh-heading-row">
          <div>
            <span className="vh-eyebrow">{t('Manufacturers available now', 'Fabricantes disponibles ahora')}</span>
            <h2>{t('Shop brands with', 'Explore marcas con')}<br />{t('products in stock.', 'productos en existencia.')}</h2>
          </div>
          <p className="vh-heading-aside">
            {t(
              'This list is connected to live inventory and only includes manufacturers with products currently available.',
              'Esta lista está conectada al inventario en vivo y solo incluye fabricantes con productos disponibles actualmente.'
            )}
          </p>
        </div>

        <div className="vh-stock-brand-grid">
          {loading ? Array.from({ length: 4 }, (_, index) => (
            <div className="vh-stock-brand-card is-loading" key={index}>
              <span />
              <div />
              <i />
            </div>
          )) : brands.map((brand) => {
            return (
              <Link
                className="vh-stock-brand-card"
                key={brand.idbrand || brand.brand_name}
                to={`/search?q=*&brand=${encodeURIComponent(brand.brand_name)}&availability=in_stock&page=1`}
              >
                <span className="vh-live-label"><i /> {t('In stock', 'En existencia')}</span>
                <StockBrandLogo brand={brand} />
                <div className="vh-stock-brand-footer">
                  <strong>{brand.brand_name}</strong>
                  <span>{t('View available products', 'Ver productos disponibles')} <b>→</b></span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="vh-centered-action">
          <Link className="vh-button vh-button-outline" to="/search?q=*&availability=in_stock&page=1">
            {t('Browse all available products', 'Explorar todos los productos disponibles')} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const { language, t } = useLanguage();
  const homeFaqs = getHomeFaqs(language);

  return (
    <main className="vh-home">
      <section className="vh-hero">
        <div className="vh-hero-backdrop">
          <img src={heroImage} alt="" aria-hidden="true" />
        </div>
        <div className="vh-container vh-hero-layout">
          <div className="vh-hero-copy">
            <span className="vh-kicker">
              <span className="vh-kicker-line" />
              {t('Industrial automation supply', 'Suministro de automatización industrial')}
            </span>
            <h1>{t('Find the right part.', 'Encuentre la pieza correcta.')}<br /><em>{t('Keep moving.', 'Siga avanzando.')}</em></h1>
            <p className="vh-hero-lead">
              {t(
                'Global automation brands, responsive technical support and dependable supply for manufacturers across the Americas.',
                'Marcas globales de automatización, soporte técnico ágil y suministro confiable para fabricantes en todo el continente americano.'
              )}
            </p>
            <div className="vh-hero-actions">
              <Link className="vh-button vh-button-primary" to="/search?q=*&inStock=true&page=1">
                {t('Shop in-stock products', 'Comprar productos en existencia')} <span aria-hidden="true">→</span>
              </Link>
              <Link className="vh-button vh-button-ghost" to="/about#contact">
                {t('Request a fast quote', 'Solicitar una cotización rápida')}
              </Link>
            </div>
            <div className="vh-hero-proof">
              <div><strong>30+</strong><span>{t('years in industry', 'años en la industria')}</span></div>
              <div><strong>10K+</strong><span>{t('products available', 'productos disponibles')}</span></div>
              <div><strong>3</strong><span>{t('regional offices', 'oficinas regionales')}</span></div>
            </div>
          </div>

          <div className="vh-hero-visual">
            <div className="vh-hero-image">
              <img src={secondaryImage} alt={t('Industrial automation equipment in a modern facility', 'Equipo de automatización industrial en una planta moderna')} />
            </div>
            <Link className="vh-availability-card" to="/search?q=*&inStock=true&page=1">
              <span className="vh-availability-icon"><i className="fas fa-box-open" /></span>
              <span>
                <small>{t('Live inventory', 'Inventario en vivo')}</small>
                <strong>{t('Ready when you are', 'Listo cuando usted lo esté')}</strong>
              </span>
              <span aria-hidden="true">↗</span>
            </Link>
          </div>
        </div>
      </section>

      <div className="vh-trust-strip" aria-label={t('Service benefits', 'Beneficios del servicio')}>
        <div className="vh-container">
          <span><i className="fas fa-globe-americas" /> {t('Global brand network', 'Red global de marcas')}</span>
          <span><i className="fas fa-bolt" /> {t('Responsive fulfillment', 'Entrega ágil')}</span>
          <span><i className="fas fa-user-cog" /> {t('Technical product guidance', 'Asesoría técnica de productos')}</span>
          <span><i className="fas fa-shield-alt" /> {t('Quality-focused sourcing', 'Abastecimiento enfocado en la calidad')}</span>
        </div>
      </div>

      <FeaturedProducts />

      <RecommendedProducts />

      <InStockBrandShowcase />

      <section className="vh-section vh-brands-section">
        <div className="vh-container">
          <div className="vh-section-heading vh-heading-row">
            <div>
              <span className="vh-eyebrow">{t('Brands, organized by need', 'Marcas organizadas según su necesidad')}</span>
              <h2>{t('Start with a manufacturer.', 'Comience con un fabricante.')}<br />{t('Finish with a solution.', 'Termine con una solución.')}</h2>
            </div>
            <p className="vh-heading-aside">
              {t(
                'Explore the product families our customers rely on for sensing, control, motion and industrial connectivity.',
                'Explore las familias de productos que nuestros clientes eligen para detección, control, movimiento y conectividad industrial.'
              )}
            </p>
          </div>

          <div className="vh-brand-grid">
            {brandCollections.map((brand, index) => (
              <Link
                to={`/search?q=*&page=1&brand=${encodeURIComponent(brand.query)}`}
                className={`vh-brand-card vh-brand-${brand.tone}`}
                key={brand.name}
              >
                <div className="vh-brand-topline">
                  <span>0{index + 1}</span>
                  <span aria-hidden="true">↗</span>
                </div>
                <div className="vh-brand-logo">
                  <img src={brand.logo} alt={t(`${brand.name} logo`, `Logotipo de ${brand.name}`)} loading="lazy" />
                </div>
                <ul>
                  {brand.categories.map(([english, spanish]) => <li key={english}>{t(english, spanish)}</li>)}
                </ul>
              </Link>
            ))}
          </div>
          <div className="vh-centered-action">
            <Link className="vh-button vh-button-outline" to="/brands">
              {t('Explore all manufacturers', 'Explorar todos los fabricantes')} <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="vh-section vh-applications-section">
        <div className="vh-container">
          <div className="vh-section-heading vh-heading-row vh-on-dark">
            <div>
              <span className="vh-eyebrow">{t('Built for the factory floor', 'Creado para la planta')}</span>
              <h2>{t('Solutions around', 'Soluciones para')}<br />{t('the way you operate.', 'su forma de operar.')}</h2>
            </div>
            <p className="vh-heading-aside">
              {t(
                'From a single replacement sensor to a multi-line controls project, our team helps reduce sourcing friction.',
                'Desde un sensor de reemplazo hasta un proyecto de control para varias líneas, nuestro equipo facilita su proceso de abastecimiento.'
              )}
            </p>
          </div>

          <div className="vh-application-grid">
            {applications.map((application) => (
              <Link className="vh-application-card" to={application.link} key={application.title[0]}>
                <img src={application.image} alt="" aria-hidden="true" loading="lazy" />
                <div className="vh-application-overlay" />
                <span className="vh-application-number">{application.number}</span>
                <div>
                  <h3>{t(...application.title)}</h3>
                  <p>{t(...application.description)}</p>
                  <span className="vh-application-link">{t('Explore application', 'Explorar aplicación')} <b>↗</b></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="vh-section vh-company-section">
        <div className="vh-container vh-company-layout">
          <div className="vh-company-media">
            <img src={facilityImage} alt={t('Valley International Supply team and facility', 'Equipo e instalaciones de Valley International Supply')} loading="lazy" />
            <div className="vh-experience-stamp">
              <strong>{t('Since', 'Desde')}</strong>
              <span>1992</span>
            </div>
          </div>

          <div className="vh-company-copy">
            <span className="vh-eyebrow">{t('More than distribution', 'Más que distribución')}</span>
            <h2>{t('A sourcing partner that speaks industrial.', 'Un socio de abastecimiento que entiende la industria.')}</h2>
            <p>
              {t(
                'We connect maintenance, engineering and procurement teams with the products and support needed to keep operations productive.',
                'Conectamos a los equipos de mantenimiento, ingeniería y compras con los productos y el soporte necesarios para mantener la productividad.'
              )}
            </p>
            <div className="vh-process-list">
              <div>
                <span>01</span>
                <div><h3>{t('Share the requirement', 'Comparta su necesidad')}</h3><p>{t('Send a part number, application challenge or complete bill of materials.', 'Envíe un número de parte, un reto de aplicación o una lista completa de materiales.')}</p></div>
              </div>
              <div>
                <span>02</span>
                <div><h3>{t('Get a clear recommendation', 'Reciba una recomendación clara')}</h3><p>{t('We evaluate availability, fit and practical alternatives with your goals in mind.', 'Evaluamos disponibilidad, compatibilidad y alternativas prácticas teniendo en cuenta sus objetivos.')}</p></div>
              </div>
              <div>
                <span>03</span>
                <div><h3>{t('Move the project forward', 'Haga avanzar su proyecto')}</h3><p>{t('Receive dependable communication from quote through fulfillment.', 'Reciba comunicación confiable desde la cotización hasta la entrega.')}</p></div>
              </div>
            </div>
            <Link className="vh-text-link" to="/about">
              {t('Meet Valley International Supply', 'Conozca Valley International Supply')} <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="vh-section vh-resources-section">
        <div className="vh-container">
          <div className="vh-section-heading vh-heading-row">
            <div>
              <span className="vh-eyebrow">{t('Plan with confidence', 'Planifique con confianza')}</span>
              <h2>{t('Useful information,', 'Información útil,')}<br />{t('not more noise.', 'sin más ruido.')}</h2>
            </div>
            <p className="vh-heading-aside">
              {t(
                'Product knowledge and practical guidance for the people responsible for keeping equipment running.',
                'Conocimiento de productos y orientación práctica para quienes mantienen los equipos en funcionamiento.'
              )}
            </p>
          </div>
          <div className="vh-resource-grid">
            {resources.map((resource) => (
              <a className={`vh-resource-card vh-resource-${resource.accent}`} href={resource.href} key={resource.title[0]}>
                <span className="vh-resource-type">{t(...resource.type)}</span>
                <h3>{t(...resource.title)}</h3>
                <p>{t(...resource.description)}</p>
                <span className="vh-resource-link">{t('Read more', 'Leer más')} <b>↗</b></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="vh-section vh-faq-section" aria-labelledby="home-faq-title">
        <div className="vh-container vh-faq-layout">
          <div className="vh-faq-intro">
            <span className="vh-eyebrow">{t('Answers before you ask', 'Respuestas antes de preguntar')}</span>
            <h2 id="home-faq-title">{t('Industrial sourcing,', 'Abastecimiento industrial,')}<br />{t('made clearer.', 'más claro.')}</h2>
            <p>
              {t('Find quick answers about our products, coverage and support, then', 'Encuentre respuestas rápidas sobre nuestros productos, cobertura y soporte; luego')}
              <Link to="/about#contact"> {t('send your requirement', 'envíe su requerimiento')}</Link> {t('when you are ready.', 'cuando esté listo.')}
            </p>
            <nav className="vh-faq-links" aria-label={t('Related Valley resources', 'Recursos relacionados de Valley')}>
              <Link to="/brands">{t('Browse manufacturers', 'Explorar fabricantes')}</Link>
              <Link to="/info">{t('Explore automation solutions', 'Explorar soluciones de automatización')}</Link>
              <a href="/services">{t('Review sourcing services', 'Revisar servicios de abastecimiento')}</a>
              <a href="/blogs">{t('Read industrial insights', 'Leer contenido industrial')}</a>
            </nav>
          </div>
          <div className="vh-faq-list">
            {homeFaqs.map((faq, index) => (
              <details key={faq.question} open={index === 0}>
                <summary><span>{String(index + 1).padStart(2, '0')}</span>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="vh-final-cta">
        <div className="vh-container vh-cta-layout">
          <div>
            <span className="vh-eyebrow">{t('Your next project starts here', 'Su próximo proyecto comienza aquí')}</span>
            <h2>{t('Tell us what the line needs.', 'Cuéntenos qué necesita su línea.')}</h2>
          </div>
          <div>
            <p>{t('Our team will help identify, source and deliver the right components for your operation.', 'Nuestro equipo le ayudará a identificar, obtener y entregar los componentes adecuados para su operación.')}</p>
            <Link className="vh-button vh-button-light" to="/about#contact">
              {t('Start a conversation', 'Iniciar una conversación')} <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
